/**
 * Iter-157K — Centralised mobile-PDF handoff card.
 *
 * Pilot polish feedback: PDFs embedded in a 390px viewport are
 * unreadable — no pinch-to-zoom, no page navigation, no rotation,
 * no document search. The pilot Hair & Makeup users explicitly
 * called this out as a trust issue: *a schedule, script revision
 * or document that cannot be comfortably read is not serving its
 * purpose.*
 *
 * Universal rule (this component is the single implementation):
 *
 *   • Mobile + PDF (or any inline-renderable file)  →  this card
 *     shows ONE big primary action that opens the file in the
 *     device's native PDF viewer / new tab.  Plus a secondary
 *     Download.
 *   • Desktop                                       →  caller still
 *     renders the embedded iframe (handled at the call site).
 *
 * Same card shape is used by DocumentViewerPage (Notes / Scene
 * Notes / Character Notes / any future PDF attachment surface) AND
 * SchedulePreviewPage so users never have to remember which
 * section they opened the PDF from.
 *
 * Props:
 *   • fileUrl     — token-signed stream URL the device can open
 *                   directly.
 *   • filename    — display name (e.g. "ep04_call_sheet.pdf").
 *   • testidPrefix— optional namespace for callers that need
 *                   per-surface testids.
 *   • subtitle    — small explainer line under the title.
 *   • downloadHref / printHref — optional overrides; default to
 *                                fileUrl with &download=1 etc.
 */
import { Download, ExternalLink, FileText } from "lucide-react";
import { Link, useLocation, useParams } from "react-router-dom";
import { useRouteBase } from "../context/ApiBaseContext";

export default function MobilePdfHandoffCard({
    fileUrl,
    filename,
    subtitle,
    testidPrefix = "mobile-pdf-handoff",
    downloadHref,
    inAppViewerHref,
}) {
    const dl = downloadHref || fileUrl;
    // Iter-158P.1 — PDF viewer chrome preservation.
    //
    // The primary "Open PDF" CTA now navigates INSIDE the OSF React
    // shell to the /projects/:projectId/pdf-viewer route, so the
    // producer keeps ProjectHeader (and an explicit ← Back control)
    // visible above the PDF. Previously this was a `target="_blank"`
    // anchor to the raw signed PDF URL, which threw the user out into
    // a headerless browser tab (the "trapped in the PDF" report from
    // the pilot).
    //
    // When a caller doesn't pass an explicit `inAppViewerHref` we
    // best-effort compute one from the current location + fileUrl.
    // Callers can still opt out by passing `inAppViewerHref={null}`
    // explicitly (rare — desktop already renders the PDF inline so
    // this component is only reached on mobile).
    const params = useParams();
    const location = useLocation();
    const routeBase = useRouteBase();
    const projectId = params?.projectId;
    let viewerHref = inAppViewerHref;
    if (viewerHref === undefined && projectId && fileUrl) {
        const qs = new URLSearchParams();
        qs.set("src", fileUrl);
        if (filename) qs.set("filename", filename);
        if (dl && dl !== fileUrl) qs.set("dl", dl);
        // Preserve the return path so the viewer's Back button lands
        // us on the exact screen the producer came from (with browser
        // scroll restoration handling their vertical position).
        qs.set("back", `${location.pathname}${location.search || ""}`);
        viewerHref = `${routeBase}/${projectId}/pdf-viewer?${qs.toString()}`;
    }
    return (
        <section
            data-testid={`${testidPrefix}-card`}
            className="mt-2 rounded-xl border border-sky-500/30 bg-sky-500/[0.06] p-5"
        >
            <div className="flex items-start gap-3">
                <div className="h-11 w-11 shrink-0 rounded-full bg-sky-500/20 text-sky-200 flex items-center justify-center">
                    <FileText size={18} />
                </div>
                <div className="min-w-0 flex-1">
                    <h2
                        data-testid={`${testidPrefix}-title`}
                        className="text-white font-semibold leading-tight text-base"
                    >
                        Open PDF in your browser viewer
                    </h2>
                    <p className="text-[11px] text-neutral-400 mt-1 leading-relaxed">
                        {subtitle ||
                            "PDFs open inside OnSetFlow so you can pinch-to-zoom and scroll, with a clear Back control at the top. Tap Device viewer if you want your phone's native full-screen PDF UI."}
                    </p>
                    {filename ? (
                        <p
                            data-testid={`${testidPrefix}-filename`}
                            className="text-[11px] text-neutral-500 mt-2 mono break-all"
                        >
                            {filename}
                        </p>
                    ) : null}
                </div>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
                {viewerHref ? (
                    <Link
                        data-testid={`${testidPrefix}-open`}
                        to={viewerHref}
                        className="tap flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 h-11 text-sm font-semibold"
                    >
                        <FileText size={14} />
                        Open PDF
                    </Link>
                ) : (
                    <a
                        data-testid={`${testidPrefix}-open`}
                        href={fileUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                        className="tap flex-1 min-w-[160px] inline-flex items-center justify-center gap-2 rounded-md bg-primary text-primary-foreground px-4 h-11 text-sm font-semibold"
                    >
                        <ExternalLink size={14} />
                        Open PDF
                    </a>
                )}
                <a
                    data-testid={`${testidPrefix}-download`}
                    href={dl}
                    download={filename || undefined}
                    className="tap inline-flex items-center justify-center gap-2 rounded-md bg-secondary border border-white/10 text-neutral-200 px-4 h-11 text-sm font-semibold"
                >
                    <Download size={14} />
                    Download
                </a>
            </div>
        </section>
    );
}
